<template>
  <VDialogV3
    ref="dialog"
    id="transport-order-card"
    show-edit-button
    height="1200px"
    width="1400px"
    :context-menu="TransportOrdersService.contextMenu"
    :entity="localTransportOrder"

    :loading="$core.utils.isNullOrUndefined(localTransportOrder)"

    @edit-button:clicked="showTransportOrderRequisitesDialog"
    @submit="onSubmit"
  >
    <template #badges>
      <VBadge v-if="localTransportOrder.isOfferBased === false" class="primary">Прямой</VBadge>
      <VBadge v-if="localTransportOrder.hasDowntime" class="warning">Есть простои</VBadge>
      <VBadge v-if="localTransportOrder.isDeleted" class="warning">Удалён</VBadge>
      <VBadge>{{ localTransportOrder.typeString }}</VBadge>
    </template>

    <header class="document-header">

      <dl>
        <dt>Грузоотправитель</dt> <dd>{{ localTransportOrder?.senderString }}</dd>
        <dt>Пункт отправки</dt> <dd>{{ localTransportOrder?.senderStore?.toString() || '--' }}</dd>
      </dl>

      <dl>
        <dt>Грузополучатель</dt>
        <dd>{{ localTransportOrder?.receiverString }}</dd>
        <dt>Пункт доставки</dt>
        <dd>{{ localTransportOrder?.receiverStore?.toString() || '--' }}</dd>
      </dl>

    </header>
    <header class="document-header">
      <dl>
        <dt>Груз</dt>
        <dd :title="localTransportOrder.getCargoUnitsStringRepresentation()">
          {{ localTransportOrder.getCargoUnitsStringRepresentation() }}
        </dd>
        <dt>ТК</dt>
        <dd>{{ localTransportOrder.deliverer?.name || '--' }},
          {{ localTransportOrder.trackingNumber || '--' }}
        </dd>
        <dt>Доставка</dt>
        <dd>
          <template v-if="localTransportOrder?.storeTemperatureRange?.isEmpty() === false">t°
            {{ localTransportOrder.storeTemperatureRange }} °C
          </template>
          <template v-else>без температурных ограничений</template>
        </dd>

      </dl>

      <dl>
        <dt>Ответственный (получатель)</dt>
        <dd>{{ localTransportOrder.responsibleReceiver?.name || '--' }}</dd>
        <dt>Задача</dt>
        <dd>
          <a
            v-if="localTransportOrder.swPortalTask && localTransportOrder.isSingleSender"
            :href="'https://cp.siberianhealth.com/task3/view.php?id=' + localTransportOrder.swPortalTask.id"
            target="_blank"
          >
            № {{ localTransportOrder.swPortalTask.id }}
          </a>
          <template v-else>--</template>
        </dd>
      </dl>

      <aside class="totals">
        <section v-if="!localTransportOrder.isSingleSender">
          <header>Стоимость перевозки</header>
          <dl>
            <dt class="total">План</dt>
            <dd>
              {{ localTransportOrder.transportCostPlanned?.format($core.currentPartner?.country.localCurrency.code) || '--' }}
            </dd>
            <dt class="total">Факт</dt>
            <dd>
              {{ localTransportOrder.transportCostActual?.format($core.currentPartner?.country.localCurrency.code) || '--' }}
            </dd>
          </dl>
        </section>

        <section>
          <header>Объявленная ценность</header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">
              {{ localTransportOrder.declaredCost?.format(localTransportOrder.declaredCostCurrencyCode) || '--' }}
            </dd>
          </dl>
        </section>
      </aside>
    </header>


    <header
      v-if="!localTransportOrder.isSingleSender"
      class="document-header"
    >
      <dl class="fill">
        <dt>Транспорт</dt>
        <dd>
          <CargoCapacity
            v-if="localTransportOrder.vehicle"
            :vehicle="localTransportOrder.vehicle"
            :cargo-weight-gross="localTransportOrder.cargoWeightGross"
            :cargo-units-count="localTransportOrder.cargoUnitsCount"
            :cargo-volume="localTransportOrder.cargoVolume"
          />
          <template v-else>--</template>
        </dd>
      </dl>
    </header>


    <!-- Assembly и Shipment — двухуровневый грид -->
    <template v-if="showShipmentDocumentsGrid">
      <h2>Отгрузочные документы</h2>
      <hr>
      <ShipmentDocumentsGrid class="fill" :transport-order="localTransportOrder"/>
      <hr>
    </template>

    <!-- Вложенные ЗТК — двухуровневый грид -->
    <template v-if="showTransportOrdersGrid">
      <h2>Вложенные ЗТК</h2>
      <hr>
      <TransportOrdersGrid class="fill" :transport-order="localTransportOrder"/>
      <hr>
    </template>

    <!-- OrderPosition и Product — плоский грид -->
    <template v-if="showProductsGrid">
      <h2>{{ localTransportOrder?.isInternal ? 'Продукты' : 'Позиции заказа' }}</h2>
      <hr>
      <ProductsGrid class="fill" :transport-order="localTransportOrder"/>
      <hr>
    </template>

    <!-- Паллетоместа -->
    <CargoUnitsGrid :cargo-units-owner="localTransportOrder"/>

    <template #footer>
      <VButton
        theme="primary"
        :disabled="submitBtnDisabled"
        @click="onSubmit"
      >
        {{ localTransportOrder.isExisting ? 'Сохранить' : 'Создать' }}
      </VButton>

      <VButton
        :disabled="localTransportOrder?.isExisting === false"
        @click="addDowntime"
      >
        Добавить простой +
      </VButton>

      <div v-if="submitErrorText" class="error">
        <VIcon material="priority_high"/>
        {{ submitErrorText }}
      </div>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./TransportOrderCard.scss"/>

<script lang="ts">

import TransportOrder         from '@/domains/TransportOrders/TransportOrder';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import TransportDowntime      from "@/domains/TransportDowntime/TransportDowntime";

import ShipmentDocumentsGrid  from "@/domains/TransportOrders/components/ShipmentDocumentsGrid.vue";
import TransportOrdersGrid    from "@/domains/TransportOrders/components/TransportOrdersGrid.vue";
import ProductsGrid           from "@/domains/TransportOrders/components/ProductsGrid.vue";
import CargoUnitsGrid         from "@/domains/CargoUnits/components/CargoUnitsGrid/CargoUnitsGrid.vue";
import CargoCapacity          from "@/domains/TransportOrders/components/CargoCapacity/CargoCapacity.vue";

export default defineComponent({
  name: 'TransportOrderCard',

  components: {
    CargoCapacity,
    CargoUnitsGrid,
    ShipmentDocumentsGrid,
    TransportOrdersGrid,
    ProductsGrid,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
    transportOrder: {
      type: TransportOrder,
    },
  },

  emits: [
    'close',
    TransportOrder.Events.Created,
    TransportOrder.Events.Updated
  ],

  setup() {
    return {
      TransportOrdersService
    }
  },

  data() {
    return {
      localTransportOrder: undefined as unknown as TransportOrder,
      toolbarContextMenuVisible: false,
      localSWPortalTask: null as any,
      receivers: null as any[] | null,
      isLoading: false,
    };
  },

  computed: {
    submitErrorText(): string | null {
      if (this.localTransportOrder?.isExisting === false) {
        if (!this.localTransportOrder?.pickupDatePlanned) return 'Не указана плановая дата забора груза';
        if (!this.localTransportOrder?.arrivalDatePlanned) return 'Не указана плановая дата прибытия груза';
        if (!this.localTransportOrder?.responsibleReceiver) return 'Не указан ответственный от получателя';
      }
      return null;
    },

    submitBtnDisabled(): boolean {
      return this.isLoading || this.submitErrorText !== null;
    },

    showShipmentDocumentsGrid(): boolean {
      return this.localTransportOrder.isSingleSender
        || this.localTransportOrder.isConsolidated;
    },

    showTransportOrdersGrid(): boolean {
      return this.localTransportOrder.isConsolidated;
    },

    showProductsGrid(): boolean {
      return this.localTransportOrder.isOrderPositionPickup
        || this.localTransportOrder.isReturn
        || this.localTransportOrder.isService
        || this.localTransportOrder.isInternal;
    },
  },

  async created() {
    await this.init();
  },

  methods: {
    async init() {
      if (this.id) {
        this.localTransportOrder = await TransportOrdersService.get(this.id)
      } else if (this.transportOrder) {
        this.localTransportOrder = this.$core.utils.cloneDeep(this.transportOrder);
      } else {
        throw new Error('Ошибка инициализации')
      }

      if (this.localTransportOrder.isExisting === false) {
        this.showTransportOrderRequisitesDialog();
      }

      await this.localTransportOrder.calculateDeclaredCost();
    },

    closeDialog(): void {
      this.$emit('close');
      this.$core.dialogs.TransportOrderCard.hide();
    },

    async onSubmit(): Promise<void> {
      this.isLoading = true;

      // Кнопка лежит в слоте #footer, дефолтный сабмит VDialogV3 не отрабатывает,
      // поэтому гейт валидации зовём руками.
      if (await this.$refs.dialog.validateAndFocus() === false) {
        this.isLoading = false;
        return;
      }

      let success: boolean;

      if (this.localTransportOrder.isExisting === true) {
        success = await TransportOrdersService.update(this.localTransportOrder);
      }
      else {
        success = await TransportOrdersService.add(this.localTransportOrder, this.receivers ?? [], this.localSWPortalTask);
      }

      this.isLoading = false;

      if (success) {
        this.$core.dialogs.TransportOrderCard.resolve();
      }
    },

    showTransportOrderRequisitesDialog(): void {
      this.$core.dialogs.TransportOrderRequisitesDialog.show({
        transportOrder: this.localTransportOrder,
        swPortalTask: this.localSWPortalTask,
        receivers: this.receivers,
      }, {
        save: ({transportOrder, SWPortalTask, receivers}: any) => {
          this.localTransportOrder = transportOrder;
          this.localSWPortalTask = SWPortalTask;
          this.receivers = receivers;
        },
        [TransportDowntime.Events.Updated]: (transportOrder) => {
          this.$emit(TransportOrder.Events.Updated);

          this.localTransportOrder = transportOrder;
        }
      });
    },

    addDowntime(): void {
      const downtime = TransportDowntime.fromTransportOrder(this.localTransportOrder.id);

      this.$core.dialogs.TransportDowntimeDialog.show({downtime}, {
        [TransportDowntime.Events.Created]: (createdDowntime: TransportDowntime) => {
          this.localTransportOrder.downtimes.push(createdDowntime);

          this.$emit(TransportOrder.Events.Updated);
        },
      });
    },


  },
});
</script>
